// AUTO-GENERATED — DO NOT EDIT. Run `npm run contract:sync`.
// Source: server/src/common/realtime-contract.ts  SOURCE-HASH: 159521da47d1b0b35e49e544658a396113ae4db2433c003b052630569bcde3f6
/**
 * Realtime event contract — the single source of truth for Socket.IO event
 * names, payload shapes, and the delivery envelope (realtime-sync-audit §4.1).
 *
 * OWNERSHIP: this file lives in server/ (the producer). web/ consumes a
 * verbatim copy at web/src/types/realtime-contract.ts, kept in sync by
 * web/scripts/sync-realtime-contract.mjs. KEEP THIS FILE DEPENDENCY-FREE —
 * types and plain constants only — so the copy compiles in both codebases.
 * Entity types (Task, Epic, Workspace, View) are generic parameters because
 * each repo has its own entity definitions.
 */

export const REALTIME_CONTRACT_VERSION = 1 as const;

export enum SocketEvent {
  taskAdded = 'taskAdded',
  taskUpdated = 'taskUpdated',
  taskDeleted = 'taskDeleted',
  taskCommentsChanged = 'taskCommentsChanged',
  taskActivityChanged = 'taskActivityChanged',
  taskDependenciesChanged = 'taskDependenciesChanged',
  tasksUpdated = 'tasksUpdated',
  tasksDeleted = 'tasksDeleted',
  epicUpdated = 'epicUpdated',
  epicDeleted = 'epicDeleted',
  spaceUpdated = 'spaceUpdated',
  sprintUpdated = 'sprintUpdated',
  planning = 'planning',
  planningChanged = 'planningChanged',
  planningAgendaChanged = 'agendaChanged',
  notificationsChanged = 'notificationsChanged',
  viewAdded = 'viewAdded',
  viewUpdated = 'viewUpdated',
  viewDefaultUpdated = 'viewDefaultUpdated',
  viewDeleted = 'viewDeleted',
  viewsOrderUpdated = 'viewsOrderUpdated',
  importProgress = 'importProgress',
  importCompleted = 'importCompleted',
  importFailed = 'importFailed',
  decisionsSwept = 'decisions:swept',
  decisionsJobStarted = 'decisions:job-started',
  decisionsJobCompleted = 'decisions:job-completed',
  decisionsJobClarification = 'decisions:job-clarification',
  decisionsJobFailed = 'decisions:job-failed',
  decisionsCreated = 'decisions:created',
}

/**
 * Every event travels in this envelope once REALTIME_ENVELOPE is on.
 * Always a plain object on the wire — never JSON.stringified.
 */
export interface RealtimeEnvelope<TData = unknown> {
  v: typeof REALTIME_CONTRACT_VERSION;
  event: SocketEvent;
  /** null for user-room events (e.g. notificationsChanged). */
  workspaceId: number | null;
  /** Per-workspace monotonic sequence — stamped from Phase 3 on; null until then. */
  seq: number | null;
  /** null for system/cron emitters. */
  actorUserId: number | null;
  /** The socket that initiated the change (x-socket-id header), if any. */
  actorSocketId: string | null;
  data: TData;
}

// ---- Concrete payload shapes (entity-independent) ----

export interface TaskDeletedPayload {
  id: number;
  type?: string;
}

export interface TasksDeletedPayload {
  tasks: number[];
}

/** Batch update. Field semantics mirror the PATCH /:spaceId/tasks/update body. */
export interface TasksUpdatedPayload {
  tasks: number[];
  stage?: string;
  archivedAt?: string | null;
  sprintId?: number | null;
  planningSessionId?: number | null;
  clearSprintStatus?: boolean;
  epicId?: number | null;
  assignee?: number | null;
  statusId?: number;
  priority?: string;
  type?: string;
  estimate?: number | null;
  storyPoints?: number | null;
  startDate?: string | null;
  deadline?: string | null;
  labelIds?: number[];
  action?: 'add' | 'remove';
}

export interface TaskIdPayload {
  id: number;
}

export interface TaskDependenciesChangedPayload {
  taskId: number;
  relatedTaskId?: number;
}

export interface EpicDeletedPayload {
  id: number;
}

export interface ImportProgressPayload {
  importId: number;
  progress: number;
  tasksCreated: number;
  totalTasks: number;
}

export interface ImportCompletedPayload {
  importId: number;
  tasksCreatedCount: number;
  labelsCreatedCount: number;
}

export interface ImportFailedPayload {
  importId: number;
  error: string;
}

export interface DecisionsSweptPayload {
  workspaceId: number;
  count: number;
}

export interface DecisionsJobStartedPayload {
  jobId: number;
  count: number;
}

export interface DecisionsJobCompletedPayload {
  jobId: number;
  decisionsCount: number;
}

export interface DecisionsJobClarificationPayload {
  jobId: number;
  messages: unknown[];
}

export interface DecisionsJobFailedPayload {
  jobId: number;
  error: string;
}

export interface DecisionsCreatedPayload {
  workspaceId: number;
}

/**
 * Event → payload map. TTask/TEpic/TWorkspace/TView are supplied by each repo:
 *  - server instantiates with its Prisma-derived types
 *  - web instantiates with src/types/{Task,Epic,Workspace,WorkspaceView}
 * "Full entity" payloads are declared as such; partials are declared Partial —
 * never an ambiguous mix (audit F13).
 */
export interface RealtimePayloads<TTask = unknown, TEpic = unknown, TWorkspace = unknown, TView = unknown> {
  [SocketEvent.taskAdded]: TTask;
  [SocketEvent.taskUpdated]: TTask;
  [SocketEvent.taskDeleted]: TaskDeletedPayload;
  [SocketEvent.taskCommentsChanged]: TaskIdPayload;
  [SocketEvent.taskActivityChanged]: TaskIdPayload;
  [SocketEvent.taskDependenciesChanged]: TaskDependenciesChangedPayload;
  [SocketEvent.tasksUpdated]: TasksUpdatedPayload;
  [SocketEvent.tasksDeleted]: TasksDeletedPayload;
  [SocketEvent.epicUpdated]: TEpic;
  [SocketEvent.epicDeleted]: EpicDeletedPayload;
  [SocketEvent.spaceUpdated]: Partial<TWorkspace> & { id: number };
  [SocketEvent.sprintUpdated]: unknown; // client invalidates only; payload unused
  [SocketEvent.planning]: unknown; // consumed by Planning views (timer/agenda state); shape is ad-hoc, typed unknown pending contract-ization
  [SocketEvent.planningChanged]: unknown; // consumed by Planning views (timer/agenda state); shape is ad-hoc, typed unknown pending contract-ization
  [SocketEvent.planningAgendaChanged]: unknown; // consumed by Planning views (timer/agenda state); shape is ad-hoc, typed unknown pending contract-ization
  [SocketEvent.notificationsChanged]: null;
  [SocketEvent.viewAdded]: TView;
  [SocketEvent.viewUpdated]: TView;
  [SocketEvent.viewDefaultUpdated]: TView;
  [SocketEvent.viewDeleted]: TView;
  [SocketEvent.viewsOrderUpdated]: TView[];
  [SocketEvent.importProgress]: ImportProgressPayload;
  [SocketEvent.importCompleted]: ImportCompletedPayload;
  [SocketEvent.importFailed]: ImportFailedPayload;
  [SocketEvent.decisionsSwept]: DecisionsSweptPayload;
  [SocketEvent.decisionsJobStarted]: DecisionsJobStartedPayload;
  [SocketEvent.decisionsJobCompleted]: DecisionsJobCompletedPayload;
  [SocketEvent.decisionsJobClarification]: DecisionsJobClarificationPayload;
  [SocketEvent.decisionsJobFailed]: DecisionsJobFailedPayload;
  [SocketEvent.decisionsCreated]: DecisionsCreatedPayload;
}
